import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { WEB_UPLOADER_PATH } from "@/content/webUploader";
import SiteFrame from "./SiteFrame";
import { APP_PATH, LOGIN_PATH, REGISTER_PATH, STUDIO_APP, STUDIO_APP_NOTIFY, STUDIO_DOWNLOADS } from "./paths";
import { ProductBadge, ProductEyebrow, ProductLockup } from "./products";
import { BAND, BTN_OUTLINE, BTN_OUTLINE_MD, BTN_PRIMARY, BTN_PRIMARY_MD, CARD, H1, SECTION_Y, TEXT_LINK, WRAP } from "./ui";

const PLATFORMS = ["mac", "ios", "windows"] as const;
type Platform = (typeof PLATFORMS)[number];

const PlatformIcon = ({ platform }: { platform: Platform }) => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {platform === "mac" && <path d="M3 5h18v11H3zM1.5 19h21" />}
    {platform === "ios" && (
      <>
        <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
        <path d="M10.5 18.5h3" />
      </>
    )}
    {platform === "windows" && <path d="M3 5.5 10.5 4.5v7H3zM12.5 4.2 21 3v8.5h-8.5zM3 13.5h7.5v7L3 19.5zM12.5 13.5H21V22l-8.5-1.2z" />}
  </svg>
);

/**
 * /download: the Photohawk Studio app, for photographers and event businesses (staging only until launch, like the
 * rest of the new site). The app launches on Mac and iPhone/iPad in November 2026, Windows later; today Studio runs
 * in the web browser, so the page's main action is "Use Studio in your browser now" and nothing offers a download.
 * Each platform's button becomes its public download once the owner fills STUDIO_DOWNLOADS (./paths); until then
 * Mac and iPhone/iPad say "Launching November 2026" (with "Get notified", an email to support) and Windows "Later".
 * The Studio app signs in with a Studio account; Photohawk Personal is a different app. Light, in the dashboard's
 * tokens (src/pages/home/ui.ts).
 */
const DownloadPage = () => {
  const { t } = useTranslation();
  const k = (key: string) => t(`home.download.${key}`);
  const s = (key: string) => t(`home.studio.app.${key}`);

  return (
    <SiteFrame page="download" path="studio">
      <section aria-labelledby="download-title">
        <div className={`${WRAP} flex flex-col gap-6 pb-[clamp(48px,6vw,80px)] pt-[clamp(32px,5vw,72px)]`}>
          <ProductEyebrow product="studio" text={s("eyebrow")} />
          <h1 id="download-title" className={`${H1} max-w-[14em]`}>
            {k("title")}
          </h1>
          <p className="m-0 max-w-[36em] text-[clamp(17px,1.5vw,20px)] leading-relaxed text-muted-foreground">{k("subtitle")}</p>
          <span className="inline-flex h-9 items-center self-start rounded-full border-[1.5px] border-accent-foreground bg-accent px-3.5 text-sm font-bold text-accent-foreground" data-testid="download-badge">
            {s("badge")}
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <Link to={REGISTER_PATH} className={BTN_PRIMARY} data-testid="download-use-browser">
              {k("useBrowser")}
            </Link>
            <Link to={LOGIN_PATH} className={BTN_OUTLINE}>
              {t("home.doors.studio.signIn")}
            </Link>
          </div>
          <Link to={STUDIO_APP} className={`${TEXT_LINK} self-start`}>
            {s("featuresLabel")} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      <section aria-labelledby="download-platforms" className={`${BAND} border-t border-border`}>
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-8`}>
        <h2 id="download-platforms" className="m-0 font-display text-[clamp(24px,2.4vw,30px)] font-bold tracking-[-0.02em]">
          {s("platformsLabel")}
        </h2>
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-5 p-0">
          {PLATFORMS.map((p) => {
            const url = STUDIO_DOWNLOADS[p];
            const later = p === "windows" && !url;
            return (
              <li key={p} className={`flex flex-col gap-4 p-7 ${later ? "rounded-2xl border border-dashed border-input bg-transparent" : CARD}`} data-testid={`download-${p}`}>
                <span className="flex items-center justify-between gap-3 text-foreground">
                  <PlatformIcon platform={p} />
                  {!url && (
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.06em] ${later ? "border-muted-foreground text-muted-foreground" : "border-accent-foreground text-accent-foreground"}`}
                      data-testid={`download-${p}-status`}
                    >
                      {later ? s("later") : k("launching")}
                    </span>
                  )}
                </span>
                <h3 className="m-0 font-display text-[22px] font-bold">{s(p)}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{k(`${p}Text`)}</p>
                <div className="mt-auto pt-1">
                  {url ? (
                    <a href={url} className={BTN_PRIMARY_MD}>
                      {k(`${p}Button`)}
                    </a>
                  ) : later ? null : (
                    <a href={STUDIO_APP_NOTIFY} className={TEXT_LINK} data-testid={`download-${p}-notify`}>
                      {s("notify")} <span aria-hidden="true">→</span>
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          <div className={`${CARD} flex flex-col gap-3 p-7`} data-testid="download-account">
            <ProductLockup product="studio" />
            <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{k("account")}</p>
            <div className="mt-auto flex flex-wrap gap-3 pt-1">
              <Link to={REGISTER_PATH} className={BTN_PRIMARY_MD}>
                {t("home.startFree")}
              </Link>
              <Link to={LOGIN_PATH} className={BTN_OUTLINE_MD}>
                {t("home.doors.studio.signIn")}
              </Link>
            </div>
          </div>
          <div className={`${CARD} flex flex-col gap-3 p-7`}>
            <h3 className="m-0 font-display text-[21px] font-semibold">{k("webTitle")}</h3>
            <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{k("webText")}</p>
            <Link to={WEB_UPLOADER_PATH} className={`${TEXT_LINK} mt-auto self-start`}>
              {k("webLink")} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
        <p className={`${CARD} m-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4 text-[15px]`} data-testid="download-not-personal">
          <ProductBadge product="personal" />
          <span>{k("notPersonal")}</span>
          <Link to={APP_PATH} className={TEXT_LINK}>
            {t("home.doors.personal.learnMore")} <span aria-hidden="true">→</span>
          </Link>
        </p>
        </div>
      </section>
    </SiteFrame>
  );
};

export default DownloadPage;
